import React, { useMemo, memo, useState, useRef, useEffect, useCallback } from "react";
import { Bug, Loader2 } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { MessageContent } from "~/components/assistant/MessageContent";
import { MessageFeedback } from "~/components/assistant/MessageFeedback";
import { MessageFeedbackInput } from "~/components/assistant/MessageFeedbackInput";
import type { Message, MessageGroup, MessageFeedbackCreate } from "~/components/assistant/types";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";

function TraceId({ traceId, debugMode }: { traceId?: string; debugMode: boolean }) {
  if (!debugMode || !traceId) {
    return null;
  }

  return (
    <div className="sticky top-0 self-start" onClick={(e) => e.stopPropagation()}>
      <CopyButton
        text={traceId}
        icon={Bug}
        tooltipText={`Copy Trace ID: ${traceId}`}
        variant="light"
        size="sm"
      />
    </div>
  );
}

/**
 * Determines if feedback UI should be shown for a message.
 * Feedback is shown for model_chat messages that:
 * - Are not currently streaming
 * - Were not cancelled
 * - Have no errors or warnings
 * - Are ready for feedback (persisted to database)
 */
function shouldShowFeedback(message: Message): boolean {
  return (
    message.kind === "model_chat" &&
    !message.streaming &&
    !message.cancelled &&
    !message.error &&
    !message.warning &&
    message.feedbackReady === true
  );
}

/** ~3 lines at 14px/22px line-height */
const COLLAPSED_MAX_HEIGHT = 70;

function CollapsibleUserMessage({ message, debugMode }: { message: Message; debugMode: boolean }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    setIsOverflowing(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
  }, [message.content]);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  // Collapse when clicking outside
  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        collapse();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isExpanded, collapse]);

  const expand = useCallback(() => {
    if (isOverflowing && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isOverflowing, isExpanded]);

  return (
    <div
      ref={contentRef}
      data-kind={message.kind}
      role={isOverflowing ? "button" : undefined}
      tabIndex={isOverflowing ? 0 : undefined}
      onClick={expand}
      onKeyDown={isOverflowing && !isExpanded ? (e) => e.key === "Enter" && expand() : undefined}
      className={cn(
        "px-3 py-2 rounded-lg select-text bg-gray-100 border-1 border-gray-300 grid w-full items-start relative",
        isOverflowing && !isExpanded && "cursor-pointer",
        {
          "grid-cols-[1fr_30px] gap-1": debugMode,
          "grid-cols-[1fr]": !debugMode,
        }
      )}
      style={{
        maxHeight: isExpanded ? "20vh" : `${COLLAPSED_MAX_HEIGHT}px`,
        overflowY: isExpanded ? "auto" : "hidden",
      }}
    >
      <MessageContent message={message} debugMode={debugMode} />
      <TraceId traceId={message.traceId} debugMode={debugMode} />

      {isOverflowing && !isExpanded && (
        <div
          className="absolute bottom-0 left-0 right-0 h-8 rounded-b-lg pointer-events-none bg-gray-100"
          style={{
            maskImage: "linear-gradient(to bottom, transparent, black)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent, black)",
          }}
        />
      )}
    </div>
  );
}

export interface ConversationViewerProps {
  /**
   * The messages to display
   */
  messages: Message[];
  /**
   * Whether the assistant is currently loading/streaming
   */
  isLoading: boolean;
  /**
   * Whether conversation history is being loaded
   */
  isLoadingHistory?: boolean;
  /**
   * The current conversation ID
   */
  currentConversationId: string | null;
  /**
   * Whether a new conversation is being created
   */
  isCreatingConversation: boolean;
  /**
   * Whether debug mode is enabled (shows trace IDs)
   */
  debugMode: boolean;
  /**
   * Agent message minimum height configuration
   */
  agentMinHeight: { id: string | null; height: number };
  /**
   * Ref handler for message elements (for scroll management)
   */
  handleMessageRef: (id: string, el: HTMLDivElement | null) => void;
  /**
   * Ref for the scroll container
   */
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Ref for the content container
   */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /**
   * The title of the context (e.g., dataset name)
   */
  contextTitle: string;
  /**
   * Optional custom title for the empty state
   */
  emptyStateTitle?: string;
  /**
   * Optional custom description for the empty state
   */
  emptyStateDescription?: string;
  /**
   * Callback for handling structured message feedback submission
   */
  onSubmitMessageFeedback?: (messageId: string, feedback: MessageFeedbackCreate) => Promise<void>;
  /**
   * Callback for clearing structured message feedback
   */
  onClearMessageFeedback?: (messageId: string) => Promise<void>;
  /**
   * Whether to show the Beta badge in the empty state
   */
  showBetaBadge?: boolean;
  /**
   * Whether to show submitted feedback (for admin review)
   */
  showSubmittedFeedback?: boolean;
  /**
   * Optional additional classes to apply to the container
   */
  containerClasses?: string;
  /**
   * Optional custom content to render in the empty state (e.g., usage guide button)
   */
  optionalEmptyStateContent?: React.ReactNode;
}

export const ConversationViewer = memo(function ConversationViewer({
  messages,
  isLoading,
  isLoadingHistory = false,
  currentConversationId,
  isCreatingConversation,
  debugMode,
  agentMinHeight,
  handleMessageRef,
  scrollContainerRef,
  contentRef,
  contextTitle,
  emptyStateTitle,
  emptyStateDescription,
  onSubmitMessageFeedback,
  onClearMessageFeedback,
  showBetaBadge = true,
  showSubmittedFeedback = false,
  containerClasses,
  optionalEmptyStateContent,
}: ConversationViewerProps) {
  // Group messages for display
  const messageGroups = useMemo(
    () =>
      messages.reduce<MessageGroup[]>((groups, message) => {
        if (message.kind === "user_chat") {
          // Start new group with user message
          groups.push({
            id: `group-${message.id}`,
            user: message,
            agent: [],
          });
        } else if (message.kind === "model_chat" && groups.length > 0) {
          // Add agent message to most recent group
          groups[groups.length - 1].agent.push(message);
        }
        return groups;
      }, []),
    [messages]
  );

  return (
    <div className="flex-1 min-h-0 relative w-full">
      <div
        className={cn(
          "h-full overflow-y-auto scrollbar-subtle",
          "[mask-image:linear-gradient(black_calc(100%-24px),transparent_100%),linear-gradient(black,black)]",
          "[mask-size:calc(100%-16px)_100%,16px_100%]",
          "[mask-position:0px_0px,100%_0px]",
          "[mask-repeat:no-repeat,no-repeat]",
          isLoading ? "scroll-auto" : "scroll-smooth"
        )}
        ref={scrollContainerRef}
      >
        <div
          ref={contentRef}
          className={cn(
            "flex flex-col gap-2 max-w-[900px] mx-auto items-start justify-start pt-0 px-4 pb-4 text-foreground text-left min-h-full",
            containerClasses
          )}
        >
          {messages.length === 0 && (isLoadingHistory || isLoading || isCreatingConversation) ? (
            <div className="flex flex-col gap-2 w-full flex-1 justify-center items-center">
              <Loader2 className="size-8 text-gray-400 animate-spin" />
            </div>
          ) : currentConversationId &&
            messages.length === 0 &&
            !isLoadingHistory &&
            !isLoading &&
            !isCreatingConversation ? (
            <div className="flex flex-col gap-2 w-full flex-1 justify-center">
              <div className="flex flex-col font-normal items-start justify-center relative shrink-0 text-sm mx-4">
                {showBetaBadge && (
                  <Badge
                    className={cn(
                      "mb-2 rounded-[6px] text-primary",
                      "px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
                    )}
                  >
                    Beta
                  </Badge>
                )}
                <div className="flex flex-col font-semibold justify-center relative shrink-0 text-[18px]">
                  <p className="block leading-[32px]">
                    {emptyStateTitle || `Explore this ${contextTitle}`}
                  </p>
                </div>
                {emptyStateDescription && (
                  <p className="block leading-[16px] my-2 text-gray-600">
                    {emptyStateDescription} {optionalEmptyStateContent}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col w-full justify-start space-y-4">
              {messageGroups.map((group) => {
                // Get custom classes for groups with submitted feedback
                const groupClasses = showSubmittedFeedback
                  ? (() => {
                      const feedbackMessage = group.agent.find(
                        (msg) => msg.messageFeedback != null
                      );
                      if (!feedbackMessage?.messageFeedback) return "";
                      const isPositive = feedbackMessage.messageFeedback.feedback_value === 1;
                      return `p-2 pb-6 border-[6px] ${isPositive ? "border-green-100" : "border-red-100"} rounded-xl`;
                    })()
                  : "";

                return (
                  <div key={group.id} id={group.id} className={cn("relative group", groupClasses)}>
                    {/* Show feedback if any agent message in this group has feedback */}
                    {showSubmittedFeedback &&
                      group.agent.some(
                        (msg) =>
                          msg.messageFeedback != null &&
                          !onSubmitMessageFeedback &&
                          !onClearMessageFeedback
                      ) && (
                        <div className="mb-2">
                          <MessageFeedback
                            message={group.agent.find((msg) => msg.messageFeedback != null)!}
                            groupId={group.id}
                          />
                        </div>
                      )}

                    {/* User message - sticky positioned */}
                    <div
                      ref={(el) => handleMessageRef(group.user.id, el)}
                      className="sticky top-0 z-10 bg-white pt-1.5"
                    >
                      <CollapsibleUserMessage message={group.user} debugMode={debugMode} />
                    </div>

                    {/* Agent messages */}
                    {group.agent.map((message) => {
                      const showFeedback = shouldShowFeedback(message);

                      // Determine if we should show read-only feedback (no handlers but has feedback)
                      const hasInteractiveHandlers =
                        onSubmitMessageFeedback && onClearMessageFeedback;

                      return (
                        <div
                          key={message.id}
                          ref={(el) => handleMessageRef(message.id, el)}
                          className="full-w select-none"
                        >
                          <div
                            data-kind={message.kind}
                            className="px-1.5 select-text flex-1"
                            style={{
                              minHeight:
                                agentMinHeight.id === message.id
                                  ? `${agentMinHeight.height}px`
                                  : undefined,
                            }}
                          >
                            <MessageContent message={message} debugMode={debugMode} />
                            {showFeedback && hasInteractiveHandlers && (
                              <MessageFeedbackInput
                                message={message}
                                onSubmit={onSubmitMessageFeedback}
                                onClear={onClearMessageFeedback}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
